import { BasketDrawer } from '@common/components/BasketDrawer/BasketDrawer';
import { calculateTotalNumberOfItemsInBasket } from '@common/components/BasketDrawer/BasketDrawer.helpers';
import { ModuleType } from '@common/components/BasketDrawer/BasketListing.types';
import { useModulesToRecommend } from '@common/components/BasketDrawer/hooks/useModulesToRecommend';
import { Button } from '@common/components/Button';
import SectionContainer from '@common/components/SectionContainer';
import { SectionHero } from '@common/components/SectionHero';
import { Spinner } from '@common/components/Spinner/Spinner';
import { Routes } from '@common/constants';
import { analytics } from '@features/analytics';
import { useGetSpaSettings } from '@features/spa';
import { useGetRestaurantConfig } from '@features/tables/hooks/useGetRestaurantConfig';
import { useGetRestaurants } from '@features/tables/hooks/useGetRestaurants';
import { useTablesSettings } from '@features/tables/hooks/useGetTablesSettings';
import { useGetTablesWeekAvailability } from '@features/tables/hooks/useGetTablesWeekAvailability';
import RestaurantBox from '@features/tables/pages/Tables/components/RestaurantBox';
import SpaStickyBar from '@features/tables/pages/Tables/components/StickyBar';
import TableConfigurationWizard from '@features/tables/pages/Tables/components/TableConfigurationWizard';
import { TableForm } from '@features/tables/pages/Tables/components/TableForm';
import {
  buildTableFormSchema,
  TableFormValues,
} from '@features/tables/pages/Tables/components/TableForm/TableForm.schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from '@lang';
import { getDatesRange } from '@utils/helpers';
import Cookie from 'js-cookie';
import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Router, useRouter } from 'shared-routing';
import { getDisabledDates } from './TablesContainer.helpers';
import { ButtonWrapper } from './TablesContainer.styles';

import { BasketDetails } from 'shared-types/Basket';
import { JourneyParams, Page } from 'shared-types/Common';
import { Property } from 'shared-types/Property';

const normalizeToString = (val: unknown): string => {
  if (Array.isArray(val)) return val[0] || '';
  if (typeof val === 'string') return val;
  return '';
};

export type TablesContainerProps = {
  openModal: boolean;
  openModalWith: string | { value: string | string[] | undefined };
};

export type TablesContainerReduxProps = {
  property: Property;
  journeyParams: JourneyParams;
  language: string;
  isResult?: boolean;
  basketDetails?: BasketDetails;
  tablesPage?: Page;
  createBasket: (...args: unknown[]) => void;
  updateBasket: (...args: unknown[]) => void;
  changeJourneyParam: (param: string, value: string | number) => void;
};

export const TablesContainer: React.FC<TablesContainerProps & TablesContainerReduxProps> = ({
  basketDetails = null,
  isResult = false,
  openModal = false,
  openModalWith = '',
  journeyParams,
  language,
  property,
  tablesPage,
  createBasket,
  updateBasket,
  changeJourneyParam,
}) => {
  const { t } = useTranslations();

  const {
    data: restaurants = null,
    isLoading: isLoadingRestaurants,
    error: restaurantsError,
  } = useGetRestaurants(property.id, language);

  const { fromDate, tableAdults, tableChildren, adults, children } = journeyParams;

  const router = useRouter();
  const [isBasketSucceeded, setIsBasketSucceeded] = useState(false);
  const [guestsInputChanged, setGuestInputChanged] = useState(false);
  const [guestsInputClosed, setGuestInputClosed] = useState(false);

  const ID = 'tables-wrapper';
  const initialAdults = tableAdults ?? adults;
  const initialChildren = tableChildren ?? children;

  const hasRestaurants = restaurants && restaurants.length > 0;

  const openModalWithValue =
    typeof openModalWith === 'object' && openModalWith !== null && 'value' in openModalWith
      ? normalizeToString(openModalWith.value)
      : normalizeToString(openModalWith);

  const tableFormSchema = useMemo(() => buildTableFormSchema(t), [t]);

  const form = useForm<TableFormValues>({
    resolver: zodResolver(tableFormSchema),
    defaultValues: {
      guests: {
        adults: String(initialAdults || 2),
        children: String(initialChildren || 0),
      },
      restaurant: openModalWithValue
        ? { value: openModalWithValue, restaurantName: undefined }
        : undefined,
      startDate: fromDate,
      visitDate: undefined,
      timeslot: null,
      promotion: null,
      note: '',
      hasDietaryRequirements: false,
      dietaryRequirements: [],
      hasCelebrationOptions: false,
      celebrationOptions: [],
    },
  });

  const restaurant = form.watch('restaurant');
  const tableFormStartDate = form.watch('startDate');

  const restaurantValue = restaurant?.value;

  const startDate = tableFormStartDate || fromDate;

  const currentRestaurant = React.useMemo(() => {
    return (restaurants || []).find(
      (rest) => rest.restaurantName === restaurantValue || rest.value === restaurantValue
    );
  }, [restaurants, restaurantValue]);

  const closedDates = currentRestaurant?.closedDates || { closedDays: [] };

  const { data: spaSettings } = useGetSpaSettings(property.id);
  const hideArrivalTimes = spaSettings?.hideArrivalTimes || false;

  const [selectedRestaurant, setSelectedRestaurant] = useState<{
    restaurantName: string;
    restaurantId: number;
  } | null>(() => {
    const current = (restaurants || []).find(
      (rest) => rest.restaurantName === restaurant?.value || rest.value === restaurant?.value
    );
    if (current && current.restaurantName && current.config?.id) {
      return {
        restaurantName: current.restaurantName,
        restaurantId: current.config.id,
      };
    }
    return null;
  });

  const [guestCounts, setGuestCounts] = useState({
    adults: initialAdults || 2,
    children: initialChildren || 0,
  });

  const startDateString = startDate
    ? typeof startDate === 'object' && startDate.format
      ? startDate.format()
      : new Date(startDate).toISOString()
    : new Date().toISOString();

  const {
    data: weekAvailabilityData = {},
    isLoading: isLoadingWeekAvailability,
    isError: weekAvailabilityError,
  } = useGetTablesWeekAvailability({
    propertyId: property.id,
    startDate: startDateString,
    restaurant: selectedRestaurant?.restaurantName || '',
    restaurantId: selectedRestaurant?.restaurantId,
    adults: guestCounts.adults,
    children: guestCounts.children,
    enabled:
      openModal && !!selectedRestaurant?.restaurantName && !!selectedRestaurant?.restaurantId,
  });

  const {
    data: restaurantConfig,
    isFetching: isLoadingRestaurantConfig,
    error: restaurantConfigError,
  } = useGetRestaurantConfig(
    property.id,
    startDateString,
    selectedRestaurant?.restaurantName || '',
    !!selectedRestaurant?.restaurantName
  );

  const { data: tablesSettings } = useTablesSettings();
  const maxPartyFallback =
    (tablesSettings?.data?.defaultParty?.adults ?? 2) +
    (tablesSettings?.data?.defaultParty?.children ?? 0);

  const dates = getDatesRange(startDate);

  const timeSlots = Object.entries(weekAvailabilityData).map(([date, data]) => ({
    date,
    data: data || {
      timeSlots: [],
      promotions: [],
      standardAvailabilityMayRequireCreditCard: false,
    },
  }));

  const promotions = Object.values(weekAvailabilityData).reduce(
    (allPromotions, data) => {
      if (data?.promotions) {
        data.promotions.forEach((promotion) => {
          if (!allPromotions.some((p) => p.id === promotion.id)) {
            allPromotions.push(promotion);
          }
        });
      }
      return allPromotions;
    },
    [] as {
      id: string | number;
      name: string;
    }[]
  );

  const disabledDates = getDisabledDates(dates, closedDates, restaurantConfig, timeSlots);
  const mergedDatesDisabled = dates.map(({ date }) => ({
    date,
    disableDate: disabledDates[date.toString()],
  }));

  useEffect(() => {
    if (openModal && mergedDatesDisabled.length > 0 && !form.getValues('visitDate')) {
      const firstAvailable = mergedDatesDisabled.find((d) => !d.disableDate);
      if (firstAvailable) {
        form.setValue('visitDate', firstAvailable.date);
      }
    }
  }, [openModal, mergedDatesDisabled]);

  useEffect(() => {
    if (openModal && hasRestaurants && openModalWithValue) {
      const matchedRestaurant = restaurants.find(
        (r) => r.value === openModalWithValue || r.restaurantName === openModalWithValue
      );
      form.setValue('restaurant', {
        value: openModalWithValue,
        restaurantName: matchedRestaurant?.restaurantName,
      } as any);
    }
  }, [openModal, openModalWithValue, restaurants]);

  useEffect(() => {
    if (currentRestaurant && openModal) {
      setSelectedRestaurant({
        restaurantName: currentRestaurant.restaurantName,
        restaurantId: currentRestaurant.config?.id,
      });
    }
  }, [currentRestaurant, openModal]);

  useEffect(() => {
    if (initialAdults !== undefined && initialChildren !== undefined) {
      setGuestCounts({
        adults: initialAdults || 2,
        children: initialChildren || 0,
      });
    }
  }, [initialAdults, initialChildren]);

  const handleGuestsInputClose = () => {
    setGuestInputClosed(true);
    setGuestInputChanged(false);
  };

  const handleGuestsInputOpen = () => {
    setGuestInputClosed(false);
  };

  const getWeekOfAvailability = (data) => {
    const restaurantLookup =
      typeof data.restaurant === 'string' ? data.restaurant : data.restaurant?.value;

    const selectedRestaurantFromData = (restaurants || []).find(
      (rest) => rest.restaurantName === restaurantLookup || rest.value === restaurantLookup
    );
    if (selectedRestaurantFromData) {
      setSelectedRestaurant({
        restaurantName: selectedRestaurantFromData.restaurantName,
        restaurantId: selectedRestaurantFromData.config?.id,
      });
      if (data.guests) {
        setGuestCounts({
          adults: parseInt(data.guests.adults, 10) || 2,
          children: parseInt(data.guests.children, 10) || 0,
        });
      }
    }
  };

  const restaurantName = restaurantValue
    ? restaurants?.find((restaurantObject) => restaurantObject.value === restaurantValue)?.label
    : '';
  const heroBackgroundUrl = tablesPage?.heroBackgroundImage?.url;

  const handleModalOpen = (restaurantValue = '') => {
    if (hasRestaurants && restaurants.length === 1 && restaurants[0].restaurantName) {
      restaurantValue = restaurants[0].restaurantName;
    }
    const route = restaurantValue
      ? {
          pathname: Routes.TABLES_BOOK_RESTAURANT,
          query: { restaurant: restaurantValue },
        }
      : {
          pathname: Routes.TABLES_BOOK,
        };

    router.push(route);
  };

  const handleModalClose = () => {
    form.reset();
    router.push({ pathname: Routes.TABLES_HOME });
  };

  const hideSuccess = () => {
    setIsBasketSucceeded(false);
    form.reset();
  };

  const showSuccess = () => {
    setIsBasketSucceeded(true);
    handleModalClose();
  };

  const addToBasket = async (data: TableFormValues): Promise<void> => {
    const basketId = Cookie.get('basketId');
    const matchedRestaurant = (restaurants || []).find(
      (r) => r.restaurantName === data.restaurant?.value || r.value === data.restaurant?.value
    );
    const restaurantLabel = matchedRestaurant?.config
      ? matchedRestaurant.config.name
      : matchedRestaurant?.label;
    const tableItem = {
      type: 'tables',
      adults: parseInt(data.guests.adults, 10),
      children: parseInt(data.guests.children || '0', 10),
      note: data.note,
      dietaryRequirements: data.dietaryRequirements,
      celebrations: data.celebrationOptions,
      bookable: {
        ids: {
          timeslot: data.timeslot,
          restaurant: matchedRestaurant?.restaurantName,
          restaurantName: restaurantLabel,
          promotionId: data.promotion?.id,
          restaurantConfig: matchedRestaurant?.config.id,
          timeslotReservationId:
            data.promotion?.timeslotReservationId ?? data.timeslotReservationId,
        },
      },
    };

    const payload: { items: unknown[] } = {
      items: [tableItem],
    };

    if (basketId) {
      const existingItems = basketDetails?.items || [];
      payload.items = [...payload.items, ...existingItems];
      await updateBasket(property.id, basketId, language, payload);
    } else {
      await createBasket(property.id, language, payload);
    }
    showSuccess();
  };

  const refreshAvailability = () => {
    const values = form.getValues();
    getWeekOfAvailability({
      ...values,
      visitDate: startDate,
      restaurant: values.restaurant,
    });
  };

  useEffect(() => {
    if (openModal && startDate) {
      refreshAvailability();
    }
  }, [openModal, startDate, restaurantValue]);

  useEffect(() => {
    if (guestsInputChanged && guestsInputClosed) {
      refreshAvailability();
      setGuestInputChanged(false);
    }
  }, [guestsInputChanged, guestsInputClosed]);

  const showBasketDrawer = isBasketSucceeded && basketDetails?.items.length;

  const modules = Object.keys(property.modulesExcludingDisabled);

  const modulesToRecommend = useModulesToRecommend({
    modules: modules as ModuleType[],
    items: basketDetails?.items ?? [],
  });

  const isError = restaurantsError || weekAvailabilityError || restaurantConfigError;

  useEffect(() => {
    if (isError) {
      Router.push({ pathname: Routes.TABLES_HOME });
    }
  }, [isError]);

  const onFormSubmit = form.handleSubmit(addToBasket as any);

  return (
    <>
      {showBasketDrawer && (
        <BasketDrawer
          currencyCode={basketDetails.total.currencyCode}
          numberOfItems={calculateTotalNumberOfItemsInBasket(basketDetails.items)}
          open={isBasketSucceeded}
          onClose={hideSuccess}
          title={t('basketDrawer.title')}
          totalCost={basketDetails.total.amount}
          items={basketDetails?.items}
          modules={modulesToRecommend}
          hideArrivalTimes={hideArrivalTimes}
        />
      )}

      <Spinner
        isActive={
          isLoadingRestaurants ||
          isLoadingRestaurantConfig ||
          isLoadingWeekAvailability ||
          form.formState.isSubmitting
        }
      />

      <TableConfigurationWizard
        isModalOpen={openModal}
        handleClose={handleModalClose}
        restaurantName={restaurantName}
      >
        {openModal && restaurants && (
          <FormProvider {...form}>
            <TableForm
              checkAvailability={getWeekOfAvailability}
              fetchRestaurantConfig={(data) => {
                getWeekOfAvailability({ ...data, visitDate: startDate });
              }}
              onSubmit={onFormSubmit}
              restaurants={restaurants}
              timeSlots={timeSlots as any}
              promotions={promotions}
              dates={mergedDatesDisabled}
              timeslotError={weekAvailabilityError}
              restaurantConfig={restaurantConfig}
              maxPartyFallback={maxPartyFallback}
              id={ID}
              tablesDisableChildBookings={
                !!property?.modulesExcludingDisabled?.tables?.features?.disableChildBookings
              }
              changeJourneyParam={changeJourneyParam}
              onGuestsInputClose={handleGuestsInputClose}
              onGuestsInputChanged={() => setGuestInputChanged(true)}
              onGuestsInputOpen={handleGuestsInputOpen}
              property={property}
            />
          </FormProvider>
        )}
      </TableConfigurationWizard>

      {!isResult && (
        <SectionHero
          backgroundUrl={heroBackgroundUrl || ''}
          summary={tablesPage?.content || ''}
          title={tablesPage?.title || ''}
          renderButton={
            <ButtonWrapper>
              <Button
                isPrimary
                onClick={() => handleModalOpen()}
                disabled={!hasRestaurants || !!restaurantsError}
              >
                {t('tables.bookTable')}
              </Button>
            </ButtonWrapper>
          }
        />
      )}
      <SectionContainer>
        {!isLoadingRestaurants && hasRestaurants && (
          <>
            {restaurants.map(({ value, label, config }, index) => {
              const parsedConfig = config || {};
              return (
                <RestaurantBox
                  key={value}
                  image={parsedConfig?.imageGallery?.length && parsedConfig.imageGallery[0].url}
                  subtitle={parsedConfig.subtitle}
                  title={parsedConfig.name || label}
                  description={parsedConfig.description}
                  menu={parsedConfig.menu}
                  isReversed={index % 2 === 1}
                  handleAddClick={() => {
                    analytics.track({
                      event: 'view_item',
                      params: {
                        items: [{ item_category: 'tables', item_id: value, item_name: label }],
                      },
                    });

                    handleModalOpen(value);
                  }}
                />
              );
            })}
          </>
        )}
      </SectionContainer>
      <SpaStickyBar restaurant={restaurantName} id={ID} isModalOpen={openModal} />
    </>
  );
};
